import { motion } from "framer-motion";
import { Package, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

export default function AllProductsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  const handleAddToCart = async (productId) => {
    try {
      await api.post("/api/cart/add", {
        medicineId: productId,
        dose: "0mg",
        quantity: 1,
      });
      showAddToCartSuccess();
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Failed to add to cart. Make sure you are logged in!");
    }
  };

  const showAddToCartSuccess = () => {
    const successEl = document.createElement("div");
    successEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10B981, #059669);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      z-index: 9999;
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
      animation: slideIn 0.3s ease;
      font-weight: 600;
    `;
    successEl.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        Added to cart successfully!
      </div>
    `;
    document.body.appendChild(successEl);
    setTimeout(() => {
      successEl.style.animation = "slideOut 0.3s ease forwards";
      setTimeout(() => document.body.removeChild(successEl), 300);
    }, 2000);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get("api/medicines/?page=1&limit=100");
        setProducts(response.data.medicines || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <section style={styles.page} >
      
      <FeatureHeader

      title = "All Products"
      subtitle= "Explore our wide range of medicines"
      onBack = {() => navigate(-1)}
/>
      {loading ? <Loader/>:
      <div style={styles.grid}>
        
        {products.map((drug) => (
              <ProductCard key={drug._id} product={drug} navigate={navigate} handleAddToCart={handleAddToCart} />
            ))}
      </div>

          }
    </section>
  );
}

const ProductCard = ({ product, navigate, handleAddToCart }) => {
  return (
    <motion.div
      style={styles.productCard}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.95 }}
    >
      <div onClick={() => navigate(`/product/${product._id}`)}>
        <div style={styles.productImageContainer}>
          <img
            src={product.images?.[0] || ''}
            alt={product.name}
            style={styles.productImage}
            loading="lazy"
          />
          <div
            style={{
              ...styles.prescriptionBadge,
              backgroundColor: product.prescriptionRequired ? '#7914f450' : '',
            }}
          >
            {product.prescriptionRequired ? "Prescription Required" : ""}
          </div>
          <motion.button
            style={styles.cartButton}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart(product._id);
            }}
            aria-label={`Add ${product.name} to cart`}
          >
            <Plus size={16} />
          </motion.button>
        </div>
        <div style={styles.productInfo}>
          <h6 style={styles.productName}>{product.name}</h6>
          <div style={styles.priceContainer}>
            {product.originalPrice > product.price && (
              <span style={styles.originalPrice}>${product.originalPrice}</span>
            )}
            <span style={styles.currentPrice}>${product.price}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "var(--bg)",
    fontFamily: "'Poppins', sans-serif",
    padding: "0px 16px 100px",
    position: "relative",
  },
  backButton: {
    position: "fixed",
    top: 20,
    left: 16,
    background: "rgba(255,255,255,0.2)",
    borderRadius: "50%",
    padding: "12px",
    fontSize: "20px",
    color: "#333",
    cursor: "pointer",
    zIndex: 10,
    transition: "background 0.2s ease",
  },
  header: {
    marginBottom: 24,
    marginTop: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    margin: 0,
    color: "#1f1a33",
  },
  subtitle: {
    marginTop: 8,
    fontSize: 14,
    color: "#6b7280",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 14,
  },
  skeletonCard: {
    height: 240,
    borderRadius: 18,
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite",
  },
  productCard: {
    backgroundColor: "transparent",
    cursor: "pointer",
  },
  prescriptionBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    padding: '3px 8px',
    borderRadius: 10,
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productImageContainer: {
    position: 'relative',
    height: '200px',
    overflow: 'hidden',
    borderRadius: '18px',
    background: '#ededf0',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cartButton: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'white',
    border: 'solid 1.5px var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.22)',
    color: 'var(--primary)',
  },
  productInfo: {
    marginTop: '5px',
    marginBottom: '2px',
    lineHeight: '1.3',
  },
  productName: {
    display: 'block',
    width: '100%',
    fontSize: '13px',
    fontWeight: '500',
    color: '#18141f',
    margin: '0',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  currentPrice: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1f1a28',
  },
  originalPrice: {
    fontSize: '11px',
    color: '#9b93ab',
    textDecoration: 'line-through',
  },
};